import { HTMLAttributes, useContext, useMemo } from 'react'
import { ColumnDef } from '@tanstack/react-table'
import { Clock, Dices } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { DataTable } from '@/components/ui/data-table'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { useTeam } from 'app/frontend/base/config/context/team'
import {
  usePageInfo,
  useTeamAuditData,
  useTeamCharacterDetails,
} from 'app/frontend/base/config/queries'
import ItemLevelBadge from 'app/frontend/base/pages/TeamDashboard/subpages/TeamRoster/components/ItemLevelBadge'
import BlizzardIcon from 'app/frontend/base/components/BlizzardIcon'
import ItemIcon from 'app/frontend/base/components/ItemIcon'
import TeamMemberLink from 'app/frontend/base/components/TeamMemberLink'
import WishLegacyPriorityDot from 'app/frontend/base/components/WishLegacyPriorityDot'
import { genericSorting } from 'app/frontend/base/config/column-definitions/util'
import { wowheadUrl } from 'app/frontend/base/helpers'
import { crestEarnableCap } from 'app/frontend/base/helpers/crestCap'
import {
  formatUnixAge,
  formatUnixTimestamp,
} from 'app/frontend/base/helpers/relativeAge'
import {
  difficulties,
  difficultiesTrackLabels,
} from 'app/frontend/types/custom/Difficulty'
import type { AuditData } from 'app/frontend/types/custom/AuditData'
import type EquippedItem from 'app/frontend/types/custom/EquippedItem'
import ItemSlot, {
  itemEquipmentSlots,
  type ItemEquipmentSlot,
} from 'app/frontend/types/custom/ItemSlot'
import type Season from 'app/frontend/types/custom/Season'
import {
  TeamWishlistItem,
  TeamWishlistItemCharacterWishlist,
} from 'app/frontend/types/custom/TeamWishlistEncounter'
import { colorByTrack } from 'app/frontend/types/custom/UpgradeTrack'
import type UpgradeTrack from 'app/frontend/types/custom/UpgradeTrack'
import { itemQualityClasses } from 'app/frontend/types/custom/ItemQuality'
import WishLegacyPriority, {
  wishLegacyPriorities,
} from 'app/frontend/types/custom/WishLegacyPriority'
import { LootContext } from '../context'
import TeamWishTooltip from './TeamWishTooltip'

export const parseItemHash = (hash: string) => {
  const match = /^#item-(\d+)$/.exec(hash)
  return match ? Number(match[1]) : undefined
}

type Props = {
  teamWishlist: wowaudit.TeamWishlist
  itemId: number
}

type CatalogItem = {
  item: TeamWishlistItem
  encounterId: number | null
  encounterName: string
}

type RankedSlotWish = {
  item: TeamWishlistItem
  wishlist: TeamWishlistItemCharacterWishlist
  value: number | null
  rank: number
  isSelected: boolean
}

const missingCurrencyTooltip =
  "This character hasn't recently uploaded a Raidbots report based on a SimC export, and does not have the RCLootCouncil_wowaudit module installed."

const slotKey = (item: TeamWishlistItem) => item.equipment_rule ?? item.slot

const numericWishValue = (
  wishlist: TeamWishlistItemCharacterWishlist,
  valueDisplay: string,
) => {
  const total = wishlist.score_by_spec.total
  if (!total) return null
  const raw = valueDisplay === 'percentage' ? total.percentage : total.score
  return typeof raw === 'number' && Number.isFinite(raw) ? raw : null
}

const legacyPriority = (
  wishlist: TeamWishlistItemCharacterWishlist,
): WishLegacyPriority | null => {
  const score = wishlist.score_by_spec.total?.score
  if (
    typeof score === 'string' &&
    (wishLegacyPriorities as readonly string[]).includes(score)
  ) {
    return score as WishLegacyPriority
  }

  for (const wish of wishlist.wishes) {
    if ((wishLegacyPriorities as readonly string[]).includes(wish.wish_type)) {
      return (
        ('upgrade_level' in wish ? wish.upgrade_level : undefined) ??
        (wish.wish_type as WishLegacyPriority)
      )
    }
  }

  return null
}

const wishesBySpec = (wishlist: TeamWishlistItemCharacterWishlist) => {
  const groups = new Map<number, typeof wishlist.wishes>()
  wishlist.wishes.forEach((wish) => {
    const current = groups.get(wish.specialization_id)
    if (current) current.push(wish)
    else groups.set(wish.specialization_id, [wish])
  })

  return [...groups.values()].sort(
    (a, b) => b[0].weight_percentage - a[0].weight_percentage,
  )
}

const specWishValue = (
  wishlist: TeamWishlistItemCharacterWishlist,
  specId: number,
  valueDisplay: string,
) => {
  const scored = wishlist.score_by_spec[String(specId)]
  const raw = valueDisplay === 'percentage' ? scored?.percentage : scored?.score
  return typeof raw === 'number' && Number.isFinite(raw) ? raw : null
}

const formatWishValue = (value: number, valueDisplay: string) => {
  if (valueDisplay === 'percentage') {
    return `${value.toLocaleString('en-US', {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    })}%`
  }

  return value.toLocaleString('en-US', { maximumFractionDigits: 0 })
}

const equipmentSlotsFor = (slot: ItemSlot): ItemEquipmentSlot[] => {
  if (slot === 'finger') return ['finger_1', 'finger_2']
  if (slot === 'trinket') return ['trinket_1', 'trinket_2']
  if (slot === 'main_hand' || slot === 'off_hand') {
    return ['main_hand', 'off_hand']
  }
  return (itemEquipmentSlots as readonly string[]).includes(slot)
    ? [slot as ItemEquipmentSlot]
    : []
}

type EquippedLine = {
  key: string
  item: EquippedItem
  track: UpgradeTrack | null
}

const knownTrack = (
  track: UpgradeTrack | null | undefined,
): UpgradeTrack | null => (track && track !== 'None' ? track : null)

const equippedLines = (
  auditData: AuditData | undefined,
  characterId: number,
  slot: ItemSlot | null,
): EquippedLine[] => {
  const gear = slot ? auditData?.[characterId]?.current_gear : undefined
  if (!gear || !slot) return []

  return equipmentSlotsFor(slot).flatMap((equipmentSlot) => {
    const item = gear[equipmentSlot]
    if (!item?.id) return []

    return [
      {
        key: equipmentSlot,
        item,
        track: knownTrack(item.upgrade_level?.track),
      },
    ]
  })
}

const rankedSlotWishes = (
  items: TeamWishlistItem[],
  selected: TeamWishlistItem,
  characterId: number,
  valueDisplay: string,
): RankedSlotWish[] => {
  const selectedSlot = slotKey(selected)
  const sameSlotItems = items.filter((item) => {
    const itemSlot = slotKey(item)
    if (!selectedSlot || !itemSlot) return item.id === selected.id
    return itemSlot === selectedSlot
  })

  const ranked = sameSlotItems.flatMap((item) => {
    const wishlist = item.wishlists.find(
      (entry) => entry.character_id === characterId,
    )
    if (!wishlist) return []

    return [
      {
        item,
        wishlist,
        value: numericWishValue(wishlist, valueDisplay),
        isSelected: item.id === selected.id,
      },
    ]
  })

  ranked.sort((a, b) => {
    const aValue = a.value ?? Number.NEGATIVE_INFINITY
    const bValue = b.value ?? Number.NEGATIVE_INFINITY
    if (aValue !== bValue) return bValue - aValue
    return a.item.name.localeCompare(b.item.name)
  })

  return ranked.map((entry, index) => ({
    ...entry,
    rank: index + 1,
  }))
}

const MissingCurrencyCell = () => (
  <Tooltip>
    <TooltipTrigger asChild>
      <span className='text-muted-foreground opacity-50'>?</span>
    </TooltipTrigger>
    <TooltipContent className='max-w-80 text-center'>
      {missingCurrencyTooltip}
    </TooltipContent>
  </Tooltip>
)

const CrestsLeftCell = ({
  characterId,
  crest,
  auditData,
  isAuditPending,
  currentSeason,
  period,
}: {
  characterId: number
  crest: UpgradeTrack
  auditData?: AuditData
  isAuditPending: boolean
  currentSeason: Season
  period: number
}) => {
  if (isAuditPending) return null

  const lastRefresh = auditData?.[characterId]?.last_refresh
  const left = lastRefresh?.[`${crest.toLowerCase()}_crests_left`]
  if (left == null || left === '') return <MissingCurrencyCell />

  const earned = Number(lastRefresh?.[`${crest.toLowerCase()}_crests`])
  const seasonCrest = currentSeason.crests.find(
    (candidate) => candidate.name === crest,
  )
  const remainingToEarn =
    seasonCrest && !currentSeason.crests_uncapped && Number.isFinite(earned)
      ? Math.max(0, crestEarnableCap(seasonCrest, period) - earned)
      : null

  const { current, possible } = trackUpgradeSteps(
    auditData?.[characterId]?.current_gear,
    crest,
  )

  return (
    <div className='flex items-center justify-end gap-2'>
      <div className='flex flex-col items-end gap-1'>
        <span className='inline-flex items-center gap-1 whitespace-nowrap'>
          {seasonCrest?.icon_item_id ? (
            <ItemIcon id={seasonCrest.icon_item_id} className='h-4 w-4' />
          ) : null}
          <span className={colorByTrack[crest]}>{left}</span>
          {remainingToEarn != null && remainingToEarn > 0 ? (
            <span className='text-xs text-muted-foreground'>
              +{remainingToEarn} to earn
            </span>
          ) : remainingToEarn === 0 ? (
            <span className='text-xs text-muted-foreground'>capped</span>
          ) : null}
        </span>
        {possible > 0 ? (
          <span className='text-sm whitespace-nowrap'>
            <span
              className={
                current === possible ? 'text-success-foreground' : undefined
              }
            >
              {current}
            </span>
            {' / '}
            {possible} upgraded
          </span>
        ) : null}
      </div>
      <CrestAgeBadge syncedAt={lastRefresh?.crests_updated_at} />
    </div>
  )
}

const trackUpgradeSteps = (
  gear: AuditData[number]['current_gear'] | undefined,
  crest: UpgradeTrack,
) => {
  let current = 0
  let possible = 0
  Object.values(gear ?? {}).forEach((item) => {
    const level = item?.upgrade_level
    if (!level || level.track !== crest || !level.total) return
    current += level.step || 0
    possible += level.total || 0
  })
  return { current, possible }
}

const CrestAgeBadge = ({
  syncedAt,
}: {
  syncedAt: string | number | undefined
}) => {
  if (syncedAt == null || syncedAt === '') return null

  return (
    <Tooltip>
      <TooltipTrigger className='inline-flex'>
        <Badge
          variant='outline'
          className='px-1.5 py-0 font-normal text-xs gap-1 whitespace-nowrap text-muted-foreground'
        >
          <Clock className='w-3 h-3' />
          {formatUnixAge(syncedAt)}
        </Badge>
      </TooltipTrigger>
      <TooltipContent>
        Crest and catalyst data synced at {formatUnixTimestamp(syncedAt)}
      </TooltipContent>
    </Tooltip>
  )
}

const TableShell = ({
  className,
  children,
}: HTMLAttributes<HTMLDivElement>) => (
  <div className={className}>{children}</div>
)

type EvaluationRow = {
  member: wowaudit.TeamMembership
  character: wowaudit.CharacterReference
  slotWishes: RankedSlotWish[]
  selectedValue: number | null
  isTrackUpgrade: boolean
  bonusRoll: boolean
}

const wishSortTier = (row: EvaluationRow) => {
  if (row.selectedValue != null) return 3
  if (row.slotWishes.length) return 2
  if (row.isTrackUpgrade) return 1
  return 0
}

const ItemEvaluation = ({ teamWishlist, itemId }: Props) => {
  const { team, guild } = useTeam()
  const { difficulty, valueDisplay, selectedSeason } = useContext(LootContext)
  const {
    data: { currentPeriod, currentSeason, gameVersion },
  } = usePageInfo()
  const { data: auditData, isPending: isAuditPending } = useTeamAuditData(
    team.id,
  )

  const crest = difficultiesTrackLabels[difficulty]
  const period = currentPeriod[guild?.realm.region ?? 'EU']

  const catalog = useMemo(() => {
    const byId = new Map<number, CatalogItem>()
    teamWishlist.encounters.forEach((encounter) => {
      encounter.items.forEach((item) => {
        if (!byId.has(item.id)) {
          byId.set(item.id, {
            item,
            encounterId: encounter.id,
            encounterName: encounter.name,
          })
        }
      })
    })
    return [...byId.values()]
  }, [teamWishlist.encounters])

  const visibleMembers = useMemo(
    () =>
      team.members?.filter((member) => member.teamRank?.wishlist_visibility) ??
      [],
    [team.members],
  )
  const { data: memberDetails } = useTeamCharacterDetails(
    team.id,
    visibleMembers.map((member) => member.characterReference.id),
  )
  const detailsById = useMemo(
    () =>
      Object.fromEntries(
        (memberDetails ?? []).map((character) => [character.id, character]),
      ),
    [memberDetails],
  )

  const selected = catalog.find((entry) => entry.item.id === itemId)

  const rows = useMemo(() => {
    if (!selected) return []

    const items = catalog.map((entry) => entry.item)
    const item = selected.item
    const showTrackUpgrades = !localStorage.getItem('hidingTrackUpgrades')
    const difficultyIndex = difficulties.indexOf(difficulty)

    return visibleMembers.flatMap((member) => {
      const character = member.characterReference
      const slotWishes = rankedSlotWishes(
        items,
        item,
        character.id,
        valueDisplay,
      )
      const selectedWish = slotWishes.find((wish) => wish.isSelected)
      if (!selectedWish && !item.eligible_classes.includes(character.classId)) {
        return []
      }

      const meta = teamWishlist.metadata.find(
        (candidate) => candidate.id === character.id,
      )
      const track = item.slot && meta ? meta.current_tracks[item.slot] : null
      const isTrackUpgrade =
        showTrackUpgrades &&
        !!item.slot &&
        track != null &&
        track > difficultyIndex &&
        !!meta &&
        item.eligible_classes.includes(meta.class_id) &&
        item.eligible_specs.includes(meta.default_spec_id ?? 0) &&
        (item.slot !== 'trinket' || track === 6)

      return {
        member,
        character,
        slotWishes,
        selectedValue: selectedWish?.value ?? null,
        isTrackUpgrade,
        bonusRoll:
          !!selectedSeason.bonus_rolls_available &&
          selected.encounterId != null &&
          character.bonusRollEncounterIds.includes(selected.encounterId),
      }
    })
  }, [
    catalog,
    difficulty,
    selected,
    selectedSeason.bonus_rolls_available,
    teamWishlist.metadata,
    valueDisplay,
    visibleMembers,
  ])

  const columns: ColumnDef<EvaluationRow>[] = useMemo(
    () => [
      {
        id: 'name',
        accessorFn: (row) => row.character.name,
        size: 180,
        meta: { className: 'pl-0' },
        ...genericSorting('text', 'Player'),
        cell: (info) => (
          <div>
            <div className='flex items-center gap-2'>
              <TeamMemberLink
                member={info.row.original.member}
                characterReference={info.row.original.character}
                iconType='both'
              />
              {info.row.original.bonusRoll ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className='ml-auto inline-flex shrink-0'>
                      <Dices size={16} className='text-info-foreground' />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>Bonus roll target</TooltipContent>
                </Tooltip>
              ) : null}
            </div>
            <ItemLevelBadge
              details={detailsById[info.row.original.character.id]}
              size='sm'
              className='mt-0.5 w-fit py-0'
            />
          </div>
        ),
      },
      {
        id: 'equipped',
        accessorFn: (row) => {
          if (!selected) return undefined
          const levels = equippedLines(
            auditData,
            row.character.id,
            selected.item.slot,
          )
            .map((piece) => piece.item.ilvl)
            .filter((level): level is number => level != null)
          return levels.length ? Math.max(...levels) : undefined
        },
        ...genericSorting('basic', 'Equipped', true),
        cell: (info) => {
          if (!selected) return null
          const equipped = equippedLines(
            auditData,
            info.row.original.character.id,
            selected.item.slot,
          )
          if (!equipped.length) {
            return <span className='text-muted-foreground'>—</span>
          }
          return (
            <div className='flex flex-col gap-1'>
              {equipped.map((piece) => (
                <div
                  key={piece.key}
                  className='flex min-w-0 items-center gap-2'
                >
                  <a
                    href={wowheadUrl(gameVersion, piece.item)}
                    target='_blank'
                    rel='noreferrer'
                    className={cn(
                      'inline-flex min-w-0 items-center gap-1.5',
                      itemQualityClasses[piece.item.quality ?? 1],
                    )}
                  >
                    <ItemIcon id={piece.item.id} className='h-5 w-5 shrink-0' />
                    <span className='truncate'>{piece.item.name}</span>
                  </a>
                  <span className='ml-auto inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap tabular-nums'>
                    {piece.track ? (
                      <span className={colorByTrack[piece.track]}>
                        {piece.track}
                      </span>
                    ) : null}
                    {piece.item.ilvl != null ? (
                      <span>{piece.item.ilvl}</span>
                    ) : null}
                  </span>
                </div>
              ))}
            </div>
          )
        },
      },
      {
        id: 'wishes',
        meta: { className: 'w-[35%]' },
        accessorFn: (row) => wishSortTier(row),
        ...genericSorting<EvaluationRow>(
          (a, b) =>
            wishSortTier(a.original) - wishSortTier(b.original) ||
            (a.original.selectedValue ?? 0) - (b.original.selectedValue ?? 0),
          'Wishes in this slot',
          true,
        ),
        cell: (info) => {
          const row = info.row.original
          if (!row.slotWishes.length) {
            return (
              <span className='italic text-muted-foreground'>
                {row.isTrackUpgrade ? 'Track upgrade only' : 'Not an upgrade'}
              </span>
            )
          }
          return (
            <div className='flex w-full flex-col gap-1 [&_button]:w-full'>
              {row.slotWishes.map((entry) => {
                const specGroups = wishesBySpec(entry.wishlist)
                const priority = legacyPriority(entry.wishlist)
                const valueClass = entry.wishlist.outdated
                  ? 'text-destructive-foreground'
                  : entry.wishlist.edited || entry.wishlist.alternative
                  ? 'text-warning-foreground'
                  : 'text-success-foreground'

                return (
                  <TeamWishTooltip
                    key={entry.item.id}
                    wishlist={entry.wishlist}
                    itemInfo={entry.item}
                  >
                    <div
                      className={cn(
                        'flex w-full min-w-0 items-center gap-1.5',
                        entry.isSelected ? 'text-base' : 'text-sm',
                      )}
                    >
                      <span
                        className={cn(
                          'w-7 shrink-0',
                          entry.isSelected
                            ? 'text-foreground'
                            : 'text-muted-foreground',
                        )}
                      >
                        #{entry.rank}
                      </span>
                      <ItemIcon
                        id={entry.item.id}
                        className='h-5 w-5 shrink-0'
                      />
                      <span
                        className={cn(
                          'min-w-0 flex-1 truncate',
                          entry.isSelected
                            ? `text-quality-${entry.item.quality}`
                            : 'text-muted-foreground',
                        )}
                        title={entry.item.name}
                      >
                        {entry.item.name}
                      </span>
                      <span className='ml-auto inline-flex shrink-0 items-center gap-2'>
                        {specGroups.map((wishes) => {
                          const spec = wishes[0]
                          const value = specWishValue(
                            entry.wishlist,
                            spec.specialization_id,
                            valueDisplay,
                          )
                          return (
                            <span
                              key={spec.specialization_id}
                              className='inline-flex items-center gap-1'
                            >
                              {wishes.some((wish) => wish.catalyst) ? (
                                <BlizzardIcon
                                  iconName='inv_progenitor_protoformsynthesis'
                                  size='small'
                                  title='Catalyst'
                                  className='rounded-none'
                                />
                              ) : null}
                              <BlizzardIcon
                                iconName={spec.specialization_icon}
                                size='small'
                                title={spec.specialization_name}
                              />
                              {value != null ? (
                                <span
                                  className={cn(
                                    'tabular-nums',
                                    valueClass,
                                    !entry.isSelected && 'opacity-80',
                                  )}
                                >
                                  {formatWishValue(value, valueDisplay)}
                                </span>
                              ) : priority ? (
                                <span
                                  className={cn(
                                    !entry.isSelected && 'opacity-80',
                                  )}
                                >
                                  <WishLegacyPriorityDot priority={priority} />
                                </span>
                              ) : null}
                            </span>
                          )
                        })}
                      </span>
                    </div>
                  </TeamWishTooltip>
                )
              })}
            </div>
          )
        },
      },
      {
        id: 'crests',
        size: 200,
        meta: { className: 'whitespace-nowrap pr-0 text-right' },
        accessorFn: (row) => {
          const value =
            auditData?.[row.character.id]?.last_refresh?.[
              `${crest.toLowerCase()}_crests_left`
            ]
          if (value == null || value === '') return undefined
          const numeric = Number(value)
          return Number.isFinite(numeric) ? numeric : undefined
        },
        ...genericSorting('basic', `${crest} crests`, true),
        cell: (info) => (
          <CrestsLeftCell
            characterId={info.row.original.character.id}
            crest={crest}
            auditData={auditData}
            isAuditPending={isAuditPending}
            currentSeason={currentSeason}
            period={period}
          />
        ),
      },
    ],
    [
      auditData,
      crest,
      currentSeason,
      detailsById,
      gameVersion,
      isAuditPending,
      period,
      selected,
      valueDisplay,
    ],
  )

  if (!selected) return null

  return (
    <DataTable
      columns={columns}
      data={rows}
      includeHeader
      className='[&_table]:table-fixed [&_table]:min-w-[60rem]'
      cellClassName='align-top'
      wrapperComponent={TableShell}
      sortingState={[{ id: 'wishes', desc: true }]}
      getRowId={(row) => String(row.character.id)}
    />
  )
}

export default ItemEvaluation
